import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Search } from "lucide-react";
import { api } from "../lib/api";

const STATUS_LABEL = {
  pending: "Under review",
  contacted: "Contacted by studio",
  signed: "Signed to roster",
  rejected: "Not progressing this round",
  archived: "Archived",
};

const STATUS_DESC = {
  pending: "Your application is in our queue. Our scouts review every submission carefully — please allow up to 14 days from submission for a decision.",
  contacted: "A member of the studio has been in touch with you, or will be shortly. Please check your email — including spam.",
  signed: "Welcome to the Ege Models family. Look out for your onboarding details by email.",
  rejected: "Thank you for trusting us with your application. We're not progressing this submission, but we encourage you to apply again in the future.",
  archived: "This application has been archived.",
};

export default function ApplicationStatus() {
  const [reference, setReference] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!reference || !email) { setError("Please fill in both fields"); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      const r = await api.get("/applications/status", { params: { reference: reference.trim(), email: email.trim() } });
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't find your application. Please double-check.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="status-page" className="pt-32">
      <section className="max-w-[900px] mx-auto px-6 lg:px-12">
        <p className="label-overline text-zinc-500">Open casting / Status</p>
        <h1 className="font-serif text-6xl md:text-7xl tracking-tighter font-light mt-6 leading-[0.9]">
          Check your<br/><span className="italic text-zinc-600">application.</span>
        </h1>
        <p className="text-zinc-700 mt-8 max-w-2xl leading-relaxed">
          Enter the reference code we sent you (e.g. <span className="font-mono">EM-A1B2C3</span>) and the email
          you applied with.
        </p>

        <form onSubmit={lookup} className="mt-12 border border-black/10 bg-white p-8 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-1">
            <Label className="label-overline text-zinc-700">Reference</Label>
            <Input
              data-testid="status-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value.toUpperCase())}
              placeholder="EM-XXXXXX"
              className="mt-2 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black font-mono"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="label-overline text-zinc-700">Email</Label>
            <Input
              data-testid="status-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="mt-2 bg-transparent border-black/20 rounded-none focus-visible:ring-0 focus-visible:border-black"
            />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <button type="submit" disabled={loading} data-testid="status-lookup" className="btn-primary">
              {loading ? <Loader2 size={14} className="animate-spin"/> : <Search size={14}/>} Look up
            </button>
          </div>
        </form>

        {error && (
          <div data-testid="status-error" className="mt-6 border border-rose-200 bg-rose-50 text-rose-900 p-5">
            {error}
          </div>
        )}

        {result && (
          <div data-testid="status-result" className="mt-10 border border-black/10 bg-white p-10">
            <p className="label-overline text-zinc-500">Reference {result.reference}</p>
            <h2 className="font-serif text-3xl mt-3">Hi {result.full_name?.split(" ")[0] || "there"}.</h2>
            <div className="mt-8 inline-block px-4 py-2 border border-black/15 label-overline text-sm">
              {STATUS_LABEL[result.status] || result.status}
            </div>
            <p className="text-zinc-700 mt-6 leading-relaxed">{STATUS_DESC[result.status] || ""}</p>
            <p className="text-xs text-zinc-500 mt-6">Submitted {new Date(result.created_at).toLocaleDateString()}</p>
          </div>
        )}

        <div className="mt-16 border-t border-black/10 pt-8 flex justify-between items-center">
          <p className="text-sm text-zinc-600">Lost your reference? <Link to="/contact" className="link-underline">Contact the studio</Link>.</p>
          <Link to="/apply" className="btn-ghost">Apply again</Link>
        </div>
      </section>
    </div>
  );
}
